<script setup lang="ts">
    import BackIcon from "@/assets/back_blue.svg";
    import KButton from "@/components/Button.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { CellContentWidth } from "@/components/KTable/helpers";
    import KTable from "@/components/KTable/KTable.vue";
    import { useStore } from "@/store";
    import { ReceptforskrivningMall } from "@/store/modules/recept/receptforskrivningMallModule";
    import CellLakemedel from "@/tabs/Receptforskrivning/cells/CellLakemedel.vue";
    import CellRekommenderat from "@/tabs/Receptforskrivning/cells/CellRekommenderat.vue";
    import ValtLakemedelDisplay from "@/tabs/Receptforskrivning/Forskrivning/Form/ValtLakemedel/ValtLakemedelDisplay.vue";
    import ValtLakemedelInfoDisplay from "@/tabs/Receptforskrivning/Forskrivning/Form/ValtLakemedel/ValtLakemedelInfoDisplay.vue";
    import { getDefaultFormValues, SilProductType } from "@/tabs/Receptforskrivning/helpers";
    import { LakemedelDisplay } from "@/tabs/Receptforskrivning/types";
    import emitter from "tiny-emitter/instance";
    import { computed, inject, ref, toRefs } from "vue";

    const store = useStore();

    const { Approved, NonAproved } = SilProductType;

    const props = defineProps<{
        receptforskrivningsId?: string;
        hasSelectedLakemedel: boolean;
        fornya?: boolean;
        tabId?: number;
    }>();

    const emit = defineEmits<{
        (event: "lakemedelSelected", payload: { value: boolean }): void;
    }>();

    const columns: any = [
        {
            field: "lakemedel",
            header: "Läkemedel",
            minWidth: CellContentWidth.m,
            bodyTemplate: CellLakemedel,
        },
        {
            field: "rekommenderat",
            minWidth: CellContentWidth.icon,
            limitWidth: true,
            bodyTemplate: CellRekommenderat,
        },
        { field: "substans", header: "Substans", minWidth: CellContentWidth.xs },
        { field: "atc", header: "ATC", minWidth: CellContentWidth.xxs, limitWidth: true },
        { field: "styrka", header: "Styrka", minWidth: CellContentWidth.xs },
        { field: "form", header: "Form" },
        { field: "forpackning", header: "Förpackning", minWidth: CellContentWidth.m },
    ];

    const { receptforskrivningsId } = toRefs(props);

    const subsetName = ref<string | undefined>();
    const total = ref<number | null>(null);
    /**
     * Tidigare valda raden (läkemedlet) för att kunna jämföra mot nya valda och se om det förändrats
     */
    const previouslySelectedRow = ref<LakemedelDisplay | null>(null);
    /**
     * Valda raden (läkemedlet)
     */
    const selectedRow = ref<LakemedelDisplay | null>(
        store.getters["lakemedel/lakemedelById"]({
            displayMode: true,
            id: store.getters["receptforskrivning/receptforskrivningById"]({
                id: receptforskrivningsId?.value,
            })?.lakemedelId,
        }) ?? null
    );
    const sokLakemedelRef = ref();
    const isLoading = ref(false);

    const lakemedel = computed(() =>
        store.getters["lakemedel/lakemedelDisplaySubset"]({
            subsetName: subsetName.value,
        })
    );

    const formRef: any = inject("formRef");

    const setHasSelectedLakemedel = ({ value }: { value: boolean }) => {
        emit("lakemedelSelected", { value });
    };

    /**
     * Sätter valt läkemedel eller rensar formulärdata och sätter defaults + valt läkemedel vid byte av läkemedel.
     * Defaultvärden sätts även vid öppnadet av ett formulär utan existerande data (se ReceptforskrivningForm.vue),
     */
    const setSelectedLakemedel = async ({ reset }: { reset: boolean }) => {
        isLoading.value = true;

        const payload: {
            values: { [key: string]: unknown };
            reset: boolean;
        } = {
            reset,
            values: {
                // lakemedelId är samma som nplPackId. Se typningen LakemedelDisplay
                lakemedelId: selectedRow.value?.id,
                nplId: selectedRow.value?.nplId,
            },
        };

        store.commit("tabs/displaySpinner", { id: props.tabId, flag: true });

        await store.dispatch("nll/fetchKodverkByLakemedel", {
            nplId: selectedRow.value?.nplId,
            lakemedelId: selectedRow?.value?.id,
        });

        store.commit("tabs/displaySpinner", { id: props.tabId, flag: false });

        if (reset) {
            payload.values = getDefaultFormValues({
                values: {
                    ...payload.values,
                    silProductType: formRef?.value?.getFieldValue?.({ name: "silProductType" }),
                },
            });
        }

        previouslySelectedRow.value = selectedRow.value;
        formRef?.value?.setFormValues(payload);
        setHasSelectedLakemedel({ value: true });

        isLoading.value = false;
    };

    const onSearchLakemedel = async ({ query }: { query: string }) => {
        if (!query) {
            return;
        }

        try {
            const { subsetName: newSubsetName, total: newTotal } = await store.dispatch("lakemedel/fetchLakemedel", {
                filterParams: { query, product_type: formRef?.value?.getFieldValue({ name: "silProductType" }) },
            });

            subsetName.value = newSubsetName;
            total.value = newTotal;
        } catch (error) {
            return [];
        }
    };

    /**
     * Vid val av rad (läkemedel i tabellen)
     */

    const onSelectedRow = ({ value }: { value?: unknown }) => {
        selectedRow.value = (value as LakemedelDisplay) ?? null;
    };

    /**
     * Öppnad dialogfönster för att fråga om användaren verkligen vill byta läkemedel och att formuläret i så fall
     * kommer rensas (och få defaultvärden).
     */
    const openChangeDialog = () => {
        emitter.emit("open-dialog", {
            type: "warning",
            description: "Vill du byta läkemedel? Om du väljer att byta så kommer allt du fyllt i rensas.",
            buttons: [
                {
                    title: "Nej",
                    type: "secondary",
                },
                {
                    title: "Ja, byt läkemedel",
                    type: "primary",
                    action: async () => {
                        formRef?.value?.setFormValues({
                            values: getDefaultFormValues({
                                values: {
                                    silProductType: formRef?.value?.getFieldValue?.({ name: "silProductType" }),
                                },
                            }),
                            reset: true,
                        });
                        setHasSelectedLakemedel({ value: false });
                    },
                },
            ],
        });
    };

    /**
     * Vid klick på knappen för att confirma val av läkemedel. Kommer antingen fylla på formuläret med ID:n för valt
     * läkemedel eller rensa och sätta defaults + valt läkemedel (om läkemedel redan valts tidigre)
     */

    const onConfirm = async () => {
        if (!selectedRow.value) {
            return;
        }

        await setSelectedLakemedel({ reset: false });
    };

    const expand = () => {
        openChangeDialog();
    };

    defineExpose({
        // Plockas upp av KForm
        formListeners: [
            {
                fieldNames: ["silProductType"],
                action: ({ values }) => {
                    // Nytt anrop om godkända/icke godkända har ändrats
                    onSearchLakemedel({ query: sokLakemedelRef?.value?.getSearchValue?.() });
                },
            },
            {
                fieldNames: ["mall"],
                action: async ({ values }) => {
                    // mall sätts till false när "Rensa allt" klickas på i ReceptforskrivningAutocomplete
                    if (values.mall === null) {
                        setHasSelectedLakemedel({ value: false });

                        return;
                    }

                    if (values.mall) {
                        const mall: ReceptforskrivningMall = store.getters["receptforskrivningMall/mallById"]({
                            id: values.mall,
                        });

                        onSelectedRow({
                            value: store.getters["lakemedel/lakemedelById"]({
                                displayMode: true,
                                id: mall.forskrivning.items[0].lakemedelId,
                            }),
                        });

                        await setSelectedLakemedel({ reset: false });
                    }
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void | Promise<void>;
        }>,
    });
</script>

<template>
    <KSection
        name="lakemedel"
        heading="Läkemedel"
        :expanded="!hasSelectedLakemedel"
        class="lakemedel-section"
        :collapsible="false"
        :number="1"
    >
        <template #collapsedContent v-if="selectedRow">
            <ValtLakemedelDisplay :lakemedel="selectedRow" />
            <KButton v-if="!fornya" type="button" @click="expand" variant="contour" size="slim">
                <img :src="BackIcon" alt="Byt läkemedel" />
                <span>Byt läkemedel</span>
            </KButton>
        </template>
        <div class="form-content">
            <KRadio
                name="silProductType"
                :options="[
                    { label: 'Godkända läkemedel', value: Approved },
                    { label: 'Ej godkända läkemedel', value: NonAproved },
                ]"
                class="span-all"
            />
            <KAutocomplete
                ref="sokLakemedelRef"
                name="sokLakemedel"
                :options="[]"
                :onSearch="onSearchLakemedel"
                class="span-all"
                label="Sök läkemedel, styrka och ATC-kod"
                :keepSearchOnBlur="true"
                :hideDropdown="true"
            />
            <!-- lakemedelId är samma som nplPackId. Se typningen LakemedelDisplay -->
            <KText name="lakemedelId" validation="required" :hidden="true" />
            <KText name="nplId" validation="required" :hidden="true" />
            <div class="form-section span-all">
                <KTable
                    v-if="typeof total === 'number'"
                    name="lakemedel"
                    :value="lakemedel"
                    :columns="columns"
                    showSelectionToggle
                    class="span-all"
                    @selected="onSelectedRow"
                    :style="{ maxHeight: '30rem' }"
                    :expandableRows="true"
                    :multipleExpandableRows="false"
                    :expandOnRowClick="true"
                    :showExpandToggle="false"
                    :fullWidth="true"
                >
                    <template #expandedRow="{ data }">
                        <ValtLakemedelInfoDisplay :lakemedel="data" />
                    </template>
                </KTable>
            </div>
            <KButton
                v-if="typeof total === 'number'"
                type="button"
                @click="onConfirm"
                size="secondary"
                variant="standard"
                :isLoading="isLoading"
            >
                <span>Välj läkemedel</span>
            </KButton>
        </div>
    </KSection>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    .lakemedel-section {
    }
</style>
